/**
 * Form section for selecting ICD-10 diagnosis codes via async ValueSet search.
 * @agentic @benchmark/core
 */

// Components
import { MultiSelect, Text, TextInput } from "@mantine/core";
import { FormField } from "@benchmark/ui/components/atomic";
import { FormSection } from "@benchmark/ui/components/atomic";

// Hooks
import { useState, useMemo, useCallback } from "react";
import { useDebounce } from "@benchmark/ui/hooks";
import { useICD10Search } from "@/hooks/shared/useICD10Search";

// Types
import type { UseFormReturnType } from "@mantine/form";
import type { ICD10Code } from "@benchmark/core/types";
import type { PatientFormData } from "@benchmark/core/types";

/**
 * Props for ICD10Section component.
 * @agentic @benchmark/core
 */
type ICD10SectionProps = {
  form: UseFormReturnType<PatientFormData>;
  title?: string;
  subtitle?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  /** Maximum number of ICD-10 codes allowed (default: 5) */
  maxCodes?: number;
};

/**
 * Renders ICD-10 code search and multi-select for diagnosis codes.
 * @agentic @benchmark/core
 */
export const ICD10Section = ({
  form,
  title = "Diagnosis Codes",
  subtitle = "Add ICD-10 diagnosis codes from the referral",
  collapsible = false,
  defaultCollapsed = false,
  maxCodes = 5,
}: ICD10SectionProps) => {
  // Search query state
  const [searchQuery, setSearchQuery] = useState("");

  // Debounce for UI state detection (matches hook's internal debouncing)
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Fetch ICD-10 search results
  const {
    data: searchResults,
    isLoading,
    clearData: clearSearchResults,
  } = useICD10Search(searchQuery);

  // Get current selected codes from form
  const selectedCodes = (form.values["icd10-codes"] || []) as ICD10Code[];

  const extractedDiagnosisCodes = form.values["_extracted-diagnosis-codes"] as
    string | undefined;

  // Build options for MultiSelect
  const options = useMemo(() => {
    // Always include currently selected codes
    const selectedOptions = selectedCodes.map((code) => ({
      value: code.code,
      label: `${code.code} - ${code.display}`,
    }));

    // If searching and have results, add them (excluding already selected)
    if (searchQuery.trim() && searchResults.length > 0) {
      const selectedCodesToExclude = new Set(selectedCodes.map((c) => c.code));
      const searchOptions = searchResults
        .filter((result) => !selectedCodesToExclude.has(result.code))
        .map((result) => ({
          value: result.code,
          label: `${result.code} - ${result.display}`,
        }));

      return [...selectedOptions, ...searchOptions];
    }

    return selectedOptions;
  }, [searchResults, searchQuery, selectedCodes]);

  // Handle value changes - convert codes to full ICD10Code objects
  const handleChange = useCallback(
    (values: string[]) => {
      const newCodes: ICD10Code[] = values.map((code) => {
        // First check existing selected codes
        const existing = selectedCodes.find((c) => c.code === code);
        if (existing) return existing;

        // Then check search results
        const fromSearch = searchResults.find((r) => r.code === code);
        if (fromSearch) {
          return { code: fromSearch.code, display: fromSearch.display };
        }

        // Fallback - use code as display
        return { code, display: code };
      });

      form.setFieldValue("icd10-codes", newCodes);
    },
    [form, selectedCodes, searchResults],
  );

  // Handle search change
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  // Handle blur - clear search results
  const handleBlur = useCallback(() => {
    clearSearchResults();
    setSearchQuery("");
  }, [clearSearchResults]);

  // UI state calculations
  const hasMinimumChars = searchQuery.trim().length >= 2;
  const debouncedHasMinimumChars = debouncedSearchQuery.trim().length >= 2;
  const isPendingSearch =
    hasMinimumChars && !debouncedHasMinimumChars && !isLoading;

  // Determine nothing found message
  const nothingFoundMessage = useMemo(() => {
    if (!hasMinimumChars) {
      return "Type at least 2 characters to search";
    }
    if (isPendingSearch || isLoading) {
      return "Searching...";
    }
    if (searchResults.length === 0) {
      return "No ICD-10 codes found";
    }
    return undefined;
  }, [hasMinimumChars, isPendingSearch, isLoading, searchResults.length]);

  return (
    <FormSection
      title={title}
      subtitle={subtitle}
      collapsible={collapsible}
      defaultCollapsed={defaultCollapsed}
      sectionProps={{ mb: "md" }}
    >
      {extractedDiagnosisCodes && (
        <FormField label="Diagnosis Codes (extracted from fax)">
          <TextInput
            value={extractedDiagnosisCodes}
            readOnly
            variant="filled"
          />
        </FormField>
      )}
      <FormField label="ICD-10 Codes">
        <MultiSelect
          placeholder={
            isLoading
              ? "Searching ICD-10 codes..."
              : "Type to search ICD-10 codes (e.g., F41 or anxiety)..."
          }
          data={options}
          value={selectedCodes.map((c) => c.code)}
          onChange={handleChange}
          onSearchChange={handleSearchChange}
          onBlur={handleBlur}
          searchable
          clearable
          maxValues={maxCodes}
          filter={({ options }) => options} // Disable client-side filtering
          nothingFoundMessage={nothingFoundMessage}
        />
        {selectedCodes.length > 0 && (
          <Text size="xs" c="dimmed" mt="xs">
            {selectedCodes.length} code{selectedCodes.length !== 1 ? "s" : ""}{" "}
            selected
          </Text>
        )}
      </FormField>
    </FormSection>
  );
};
